import { z } from 'zod'
import type { Database } from '~/shared/common/supabase/database.types'
import { getFileUrl } from '~/server/common/supabase/file-url'

type UserRow = Database['public']['Tables']['identity_user']['Row']
type MembershipRow = Database['public']['Tables']['identity_membership']['Row']
type VenueGroupRow = Database['public']['Tables']['main_venuegroup']['Row']
type CustomerSubscriptionRow = Database['public']['Tables']['identity_customersubscription']['Row']

// --- Leaf boundary schemas (table rows) ---

// Only plan + status are read from the subscription row.
export const UserCustomerSubscriptionFromSupabase = z.object({
  plan: z.string(),
  status: z.string(),
}) satisfies z.ZodType<
  Pick<CustomerSubscriptionRow, 'plan' | 'status'>,
  Pick<CustomerSubscriptionRow, 'plan' | 'status'>
>

export type UserCustomerSubscription = z.output<typeof UserCustomerSubscriptionFromSupabase>

// Flat VenueGroup shape produced by the user query's entity join.
export const UserVenueGroupFromSupabase = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string(),
  max_latitude: z.number(),
  min_latitude: z.number(),
  max_longitude: z.number(),
  min_longitude: z.number(),
}) satisfies z.ZodType<
  Pick<VenueGroupRow, 'id' | 'name' | 'slug' | 'max_latitude' | 'min_latitude' | 'max_longitude' | 'min_longitude'>,
  Pick<VenueGroupRow, 'id' | 'name' | 'slug' | 'max_latitude' | 'min_latitude' | 'max_longitude' | 'min_longitude'>
>

export type UserVenueGroup = z.output<typeof UserVenueGroupFromSupabase>

export const UserMembershipFromSupabase = z.object({
  entity_id: z.number(),
  membership_type: z.string().nullable(),
  is_active_today: z.boolean(),
}) satisfies z.ZodType<
  Pick<MembershipRow, 'entity_id' | 'membership_type' | 'is_active_today'>,
  Pick<MembershipRow, 'entity_id' | 'membership_type' | 'is_active_today'>
>

// --- Joined query shape (union of table rows + nested joins) ---
//
// This mirrors exactly what PostgREST returns for the `getUser` query.
// We don't `satisfies` against a single Row type here because the shape is
// cross-table; per-table safety is enforced by the leaves above and by the
// flat-column pick on UserRowWithJoinsFromSupabase below.

const EntityVenueGroupRawFromSupabase = z.object({
  venue_group: UserVenueGroupFromSupabase,
})

const UserActiveEntityRawFromSupabase = z.object({
  name: z.string(),
  type: z.string().nullable(),
  legal_name: z.string().nullable(),
  features: z.array(z.string()).nullable(),
  presentation_templates: z.array(z.string()).nullable(),
  default_event_space_columns: z.array(z.string()).nullable(),
  subscription_link_override: z.string().nullable(),
  llm_prompts_json: z.unknown().nullable(),
  // Joined from file_fileupload via identity_entity.avatar_id. Used to derive
  // active_entity.avatar_url on the domain shape.
  avatar: z
    .object({
      upload: z.string().nullable(),
      filename: z.string().nullable(),
      category: z.string().nullable(),
    })
    .nullable(),
  venue_groups: z.array(EntityVenueGroupRawFromSupabase),
  // PostgREST returns one-to-many joins as arrays; accept either shape.
  customer_subscription: z
    .array(UserCustomerSubscriptionFromSupabase)
    .or(UserCustomerSubscriptionFromSupabase)
    .nullable(),
})

const UserFlatColumns = z.object({
  id: z.number(),
  supabase_user_id: z.string().nullable(),
  is_active: z.boolean(),
  is_staff: z.boolean(),
  is_superuser: z.boolean(),
  title: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  office_phone: z.string().nullable(),
  mobile_phone: z.string().nullable(),
  email: z.string().nullable(),
  active_entity_id: z.number().nullable(),
}) satisfies z.ZodType<
  Pick<
    UserRow,
    | 'id'
    | 'supabase_user_id'
    | 'is_active'
    | 'is_staff'
    | 'is_superuser'
    | 'title'
    | 'first_name'
    | 'last_name'
    | 'office_phone'
    | 'mobile_phone'
    | 'email'
    | 'active_entity_id'
  >,
  Pick<
    UserRow,
    | 'id'
    | 'supabase_user_id'
    | 'is_active'
    | 'is_staff'
    | 'is_superuser'
    | 'title'
    | 'first_name'
    | 'last_name'
    | 'office_phone'
    | 'mobile_phone'
    | 'email'
    | 'active_entity_id'
  >
>

export const UserRowWithJoinsFromSupabase = UserFlatColumns.extend({
  active_entity: UserActiveEntityRawFromSupabase.nullable(),
  active_venue_group: UserVenueGroupFromSupabase.nullable(),
  memberships: z.array(UserMembershipFromSupabase).optional().default([]),
})

// --- Domain shape ---

export const UserActiveEntity = z.object({
  id: z.number().nullable(),
  name: z.string().optional(),
  type: z.string().nullable().optional(),
  legal_name: z.string().nullable().optional(),
  customer_subscription: UserCustomerSubscriptionFromSupabase.nullable().optional(),
  features: z.array(z.string()).nullable().optional(),
  presentation_templates: z.array(z.string()).nullable().optional(),
  default_event_space_columns: z.array(z.string()).nullable().optional(),
  venue_groups: z.array(
    UserVenueGroupFromSupabase.extend({
      venue_group: UserVenueGroupFromSupabase,
    }),
  ),
  subscription_link_override: z.string().nullable().optional(),
  llm_prompts_json: z.record(z.string(), z.unknown()),
  // Optional avatar URL derived from identity_entity.avatar_id →
  // file_fileupload (category = 'entity_avatar'), run through getFileUrl().
  // Surfaces on entity-branded UI (e.g. "{Org} Tags" pill rows).
  avatar_url: z.string().nullable().optional(),
})

export type UserActiveEntity = z.output<typeof UserActiveEntity>

export const UserSchema = z.object({
  id: z.number(),
  username: z.string().nullable(),
  is_active: z.boolean(),
  is_staff: z.boolean(),
  is_superuser: z.boolean(),
  title: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  office_phone: z.string().nullable(),
  mobile_phone: z.string().nullable(),
  email: z.string().nullable(),
  active_entity: UserActiveEntity,
  current_venue_group: UserVenueGroupFromSupabase.optional(),
  active_venue_group: z.number().optional(),
  membership_type: z.string(),
})

export type User = z.output<typeof UserSchema>

// Boundary transform: joined Supabase row → domain User.
// Mirrors the legacy `getUser` mapping exactly.
export const userFromSupabaseRow = (raw: unknown): User => {
  const data = UserRowWithJoinsFromSupabase.parse(raw)
  const entity = data.active_entity

  const rawSubscription = entity?.customer_subscription ?? null
  const firstSubscription = Array.isArray(rawSubscription) ? rawSubscription[0] ?? null : rawSubscription
  const subscription: UserCustomerSubscription | null =
    entity?.type === 'client_open_use'
      ? { plan: 'early_access', status: 'active' }
      : firstSubscription

  const llmPrompts: Record<string, unknown> =
    entity?.llm_prompts_json && typeof entity.llm_prompts_json === 'object' && !Array.isArray(entity.llm_prompts_json)
      ? (entity.llm_prompts_json as Record<string, unknown>)
      : {}

  // Build the entity avatar URL from the joined file_fileupload row.
  // Only honour rows tagged as entity_avatar so a misuse of the FK can't leak
  // an unrelated file through this surface.
  const avatar = entity?.avatar
  const avatarUrl =
    avatar && avatar.upload && avatar.category === 'entity_avatar'
      ? getFileUrl(avatar.upload, avatar.filename)
      : null

  const venueGroups = (entity?.venue_groups ?? []).map((vg) => ({
    venue_group: vg.venue_group,
    id: vg.venue_group.id,
    name: vg.venue_group.name,
    slug: vg.venue_group.slug,
    max_latitude: vg.venue_group.max_latitude,
    min_latitude: vg.venue_group.min_latitude,
    max_longitude: vg.venue_group.max_longitude,
    min_longitude: vg.venue_group.min_longitude,
  }))
  const activeMembership = data.memberships.find(
    (membership) => membership.entity_id === data.active_entity_id && membership.is_active_today,
  )

  return UserSchema.parse({
    id: data.id,
    username: data.supabase_user_id,
    is_active: data.is_active,
    is_staff: data.is_staff,
    is_superuser: data.is_superuser,
    title: data.title,
    first_name: data.first_name,
    last_name: data.last_name,
    office_phone: data.office_phone,
    mobile_phone: data.mobile_phone,
    email: data.email,
    active_entity: {
      id: data.active_entity_id,
      name: entity?.name,
      type: entity?.type,
      legal_name: entity?.legal_name,
      customer_subscription: subscription,
      features: entity?.features,
      presentation_templates: entity?.presentation_templates,
      default_event_space_columns: entity?.default_event_space_columns,
      venue_groups: venueGroups,
      subscription_link_override: entity?.subscription_link_override,
      llm_prompts_json: llmPrompts,
      avatar_url: avatarUrl,
    },
    current_venue_group: data.active_venue_group ?? undefined,
    active_venue_group: data.active_venue_group?.id,
    membership_type: activeMembership?.membership_type ?? '',
  })
}

// ---------- Update ----------

// App-level patch accepted by updateUser.
// DB-backed fields (active_venue_group, first_name, ...) are persisted.
// `current_venue_group` is a client-only optimistic field — it mirrors the
// joined venue group row so the UI can reflect the change instantly while
// the refresh fetches the server-authoritative copy.
export const UserUpdatePatch = z.object({
  active_venue_group: z.number().nullable().optional(),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  office_phone: z.string().nullable().optional(),
  mobile_phone: z.string().nullable().optional(),
  current_venue_group: UserVenueGroupFromSupabase.optional(),
})

export type UserUpdatePatch = z.infer<typeof UserUpdatePatch>

// Translate a domain-level patch into the Supabase column shape.
export const userPatchToSupabase = (patch: UserUpdatePatch): Database['public']['Tables']['identity_user']['Update'] => {
  const row: Database['public']['Tables']['identity_user']['Update'] = {}
  if (Object.prototype.hasOwnProperty.call(patch, 'active_venue_group')) {
    row.active_venue_group_id = patch.active_venue_group ?? null
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'first_name')) {
    row.first_name = patch.first_name ?? null
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'last_name')) {
    row.last_name = patch.last_name ?? null
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'email')) {
    row.email = patch.email ?? null
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'title')) {
    row.title = patch.title ?? null
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'office_phone')) {
    row.office_phone = patch.office_phone ?? null
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'mobile_phone')) {
    row.mobile_phone = patch.mobile_phone ?? null
  }
  return row
}
